import { ProviderClaim } from '../../api/server/models/ProviderClaim';
import { ProviderPatientStatement } from '../../api/server/models/ProviderPatientStatement';
import { formatDate } from '../../services/primitives/date-utils';
import { parseMoney } from './utils/statement-format.utils';
import { resolveStatementClaimFinancials } from './utils/statement-claim-financials.utils';

export interface BalanceBreakdown {
    currentBalance: number;
    totalCharged: number;
    insurancePaid: number;
    planAdjustments: number;
    patientResponsibility: number;
    patientPaid: number;
    claimCount: number;
    patientPaymentCount: number;
    openClaimCount: number;
    statementDate?: string;
    paidUp: boolean;
}

/** Build balance hero metrics from the patient statement already loaded on the page. */
export const computeBalanceBreakdownFromStatement = (
    statement: ProviderPatientStatement | undefined,
    balanceOverride?: string,
    providerClaimsById?: Map<string, ProviderClaim>,
): BalanceBreakdown => {
    const claims = statement?.claims || [];
    let totalCharged = 0;
    let insurancePaid = 0;
    let planAdjustments = 0;
    let patientPaid = 0;
    let patientPaymentCount = 0;
    let openClaimCount = 0;
    let patientResponsibility = 0;

    for (const claim of claims) {
        const providerClaim = claim.id ? providerClaimsById?.get(claim.id) : undefined;
        const financials = resolveStatementClaimFinancials(claim, providerClaim);
        totalCharged += financials.charge;
        insurancePaid += financials.insurance;
        planAdjustments += financials.adjustment;
        patientResponsibility += financials.patientResp;

        for (const payment of claim.patientPayments || []) {
            const amount = parseMoney(payment.amount?.amount);
            patientPaid += amount;
            if (amount !== 0) patientPaymentCount += 1;
        }
        for (const discount of claim.discountLines || []) {
            patientPaid += parseMoney(discount.amount?.amount);
        }
        if (parseMoney(claim.balanceDue?.amount) > 0.005) {
            openClaimCount += 1;
        }
    }

    const currentBalance = parseMoney(balanceOverride ?? statement?.balanceDue?.amount);
    const paidUp = currentBalance <= 0.005;
    const formattedStatementDate = formatDate(new Date(), 'yyyy-MM-dd');
    const statementDate = formattedStatementDate === '–' ? undefined : formattedStatementDate;

    return {
        currentBalance: Math.max(0, currentBalance),
        totalCharged,
        insurancePaid,
        planAdjustments,
        patientResponsibility: Math.max(0, patientResponsibility),
        patientPaid,
        claimCount: claims.length,
        patientPaymentCount,
        openClaimCount,
        statementDate,
        paidUp,
    };
};
